'use client'

import { useCallback, useEffect } from 'react'

function isMacOS() {
  var userAgent = typeof window === 'object' ? window.navigator.userAgent.toLowerCase() : ''
  return (
    userAgent.indexOf('mac') >= 0 &&
    userAgent.indexOf('os') >= 0 &&
    !(userAgent.indexOf('iphone') >= 0 || userAgent.indexOf('ipad') >= 0 || userAgent.indexOf('windows') >= 0)
  )
}

export type KeyboardCommandModifier =
  | 'altKey' // option on mac
  | 'ctrlKey'
  | 'metaKey' // command on mac
  | 'shiftKey'

export type KeyboardCommandModifiers = {
  [Key in KeyboardCommandModifier]?: boolean
}

export function keyboardInputRepresentation(
  key: string,
  modifiers:
    | KeyboardCommandModifiers
    | {
        macOS: KeyboardCommandModifiers
        other: KeyboardCommandModifiers
      }
) {
  if ('macOS' in modifiers) {
    modifiers = isMacOS() ? modifiers.macOS : modifiers.other
  }

  if (isMacOS()) {
    let representation = key.toUpperCase()
    if (modifiers.metaKey) {
      representation = `⌘${representation}`
    }
    if (modifiers.altKey) {
      representation = `⌥${representation}`
    }
    if (modifiers.ctrlKey) {
      representation = `⌃${representation}`
    }
    return representation
  } else {
    let representation = [key.toUpperCase()]
    if (modifiers.shiftKey) {
      representation = ['Shift', ...representation]
    }
    if (modifiers.altKey) {
      representation = ['Alt', ...representation]
    }
    if (modifiers.ctrlKey) {
      representation = ['Ctrl', ...representation]
    }
    return representation
  }
}

export function useKeydownCommand(
  key: string,
  modifiers:
    | KeyboardCommandModifiers
    | {
        macOS: KeyboardCommandModifiers
        other: KeyboardCommandModifiers
      },
  action: () => void
) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Dead' && /[a-zA-Z]/.test(key)) {
        if (event.code !== `Key${key.toUpperCase()}`) {
          return
        }
      } else {
        if (event.key !== key) {
          return
        }
      }
      let effectiveModifiers
      if ('macOS' in modifiers) {
        effectiveModifiers = isMacOS() ? modifiers.macOS : modifiers.other
      } else {
        effectiveModifiers = modifiers
      }
      if ((effectiveModifiers.altKey ?? false) !== event.altKey) {
        return
      }
      if ((effectiveModifiers.ctrlKey ?? false) !== event.ctrlKey) {
        return
      }
      if ((effectiveModifiers.metaKey ?? false) !== event.metaKey) {
        return
      }
      if ((effectiveModifiers.shiftKey ?? false) !== event.shiftKey) {
        return
      }
      event.preventDefault()
      action()
    },
    [key, modifiers, action]
  )
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  return keyboardInputRepresentation(key, modifiers)
}
